import { type WalletClient, type TransactionRequest, encodeFunctionData, parseUnits } from "viem";
import { type TransactionHash, TOKEN_IDS } from "@/types";
import { gatewayV1Abi } from "@/abi";
import { TOKEN_CONFIG, getGatewayContractAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";

/**
 * Prepares a mint transaction
 */
export const prepareMintTransaction = async (
    amount: number,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const amountBigInt = parseUnits(`${amount}`, TOKEN_CONFIG[TOKEN_IDS.USDC].decimals);
    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "mint",
            args: [walletClient.account.address, amountBigInt],
        }),
    };
};

/**
 * Executes a mint transaction
 */
export const gatewayMintTransaction = async (amount: number, walletClient: WalletClient): Promise<TransactionHash> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    // Prepare the transaction request and send it using the utility function
    const txRequest = await prepareMintTransaction(amount, walletClient);

    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};

/**
 * Estimates gas for a mint transaction
 */
export const estimateMintTransactionGas = async (amount: number, walletClient: WalletClient): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareMintTransaction(amount, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};
